/**
 * Hours burned against the project's budget, cumulatively.
 *
 * The budget in NOS is set once for the DEMAND, not per month -- every period
 * row carries the same figure. Charting it as paired monthly bars therefore
 * drew the same budget twelve times beside one month of hours each, and
 * concluded that a project 11.5% over budget "came in at or under budget"
 * every month. A burn-up is the shape the data actually supports: hours
 * accumulate, the budget is one horizontal line, and the crossing point is the
 * month the project went over.
 *
 * Hours, not dollars -- the budget's own unit.
 */
import { useState } from "react";
import {
  BENCHMARK,
  ChartFrame,
  EmptyChart,
  GRID,
  niceBounds,
  SERIES_1,
  useChartWidth,
} from "./chart-primitives";

const BASE_W = 720;
const H = 220;
const PAD = { top: 14, right: 16, bottom: 28, left: 52 };

export interface BurnPoint {
  label: string;
  actualHours: number;
  /** The period this point is, so a click can be resolved back to a month. */
  id?: string;
}

const hours = (n: number) =>
  n >= 1000 ? `${Math.round(n / 1000).toLocaleString()}k` : `${Math.round(n).toLocaleString()}`;

export function BudgetBurn({
  periods,
  budgetHours,
  variancePct,
  rangeLabel,
  title = "Hours burned against budget",
  subtitle,
  onSelect,
}: {
  periods: BurnPoint[];
  /** The project's single budget, for the WHOLE project. Null when none is set. */
  budgetHours: number | null;
  /**
   * Project-to-date variance, straight from the API.
   *
   * The chart must not work out its own over/under verdict. Its line is the
   * hours in the SELECTED RANGE, while the budget line covers the whole
   * project: on a project whose range starts after it began, the two subtract
   * to "547h remaining" on a demand the API reports 11.5% OVER. Two numbers,
   * one fact, and the one derived here is the wrong one. Null when the API has
   * no variance -- then the footer says so rather than inventing one.
   */
  variancePct?: number | null;
  /** Which months the line covers, so "burned" is never read as project-to-date. */
  rangeLabel?: string | null;
  title?: string;
  subtitle?: string;
  /**
   * Drill into one month of the burn-up.
   *
   * The point carries its own cumulative, because that is the figure the chart
   * put on screen and the drill-in has to agree with it -- re-summing the
   * series at the call site is how the modal and the chart come to disagree
   * about the same month.
   */
  onSelect?: (point: BurnPoint & { cumulative: number }) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const [frame, W] = useChartWidth(BASE_W);

  if (budgetHours === null || periods.length === 0) {
    return (
      <ChartFrame title={title} subtitle={subtitle}>
        <EmptyChart message="No budget on record for this project" />
      </ChartFrame>
    );
  }

  let running = 0;
  const points = periods.map((p) => {
    running += p.actualHours;
    return { ...p, cumulative: running };
  });
  const total = running;

  const [lo, hi] = niceBounds([0, ...points.map((p) => p.cumulative), budgetHours]);
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const x = (i: number) =>
    PAD.left + (points.length === 1 ? innerW / 2 : (innerW / (points.length - 1)) * i);
  const y = (v: number) => PAD.top + innerH - ((v - lo) / (hi - lo || 1)) * innerH;
  const ticks = [lo, lo + (hi - lo) / 2, hi];

  // The first month the line crosses the budget. Safe to name even though the
  // line only covers the selected range: crossing inside the range means the
  // project crossed at or before that month, never after. Nothing is said when
  // it does not cross, because a shorter range explains that just as well as
  // staying under budget does.
  const crossing = points.find((p) => p.cumulative > budgetHours) ?? null;
  const over = variancePct !== null && variancePct !== undefined && variancePct > 0;

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      footer={
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="flex items-center gap-1.5">
            <span aria-hidden className="inline-block h-px w-4" style={{ background: BENCHMARK }} />
            Budget {hours(budgetHours)}h, whole project
          </span>
          <span className="before:mr-3 before:text-border before:content-['·']">
            {hours(total)}h burned in {rangeLabel ?? "the selected range"}
            {crossing ? `, crossing the budget in ${crossing.label}` : ""}
          </span>
          <span className="before:mr-3 before:text-border before:content-['·']">
            {variancePct === null || variancePct === undefined
              ? "Project to date: no variance on record"
              : `Project to date: ${Math.abs(variancePct).toFixed(1)}% ${over ? "over" : "under"} budget`}
          </span>
        </span>
      }
    >
      <div ref={frame} className="relative w-full overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 420 }} role="img">
          <title>{title}</title>
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y(t)}
                y2={y(t)}
                stroke={GRID}
                strokeWidth={1}
              />
              <text
                x={PAD.left - 8}
                y={y(t) + 3}
                textAnchor="end"
                className="fill-muted-foreground"
                style={{ fontSize: 10 }}
              >
                {hours(t)}
              </text>
            </g>
          ))}

          {/* The budget is a reference line, never a second series -- it is a
              constant, and drawing it as data invites reading it as data. */}
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={y(budgetHours)}
            y2={y(budgetHours)}
            stroke={BENCHMARK}
            strokeWidth={1.5}
            strokeDasharray="5 4"
          />

          <polyline
            fill="none"
            stroke={SERIES_1}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            points={points.map((p, i) => `${x(i)},${y(p.cumulative)}`).join(" ")}
          />
          {/* One hit band per month, full height, rather than the 3px dots:
              a 6-pixel target is not a control. The band is transparent, so
              the chart looks unchanged until the pointer is over it. */}
          {points.map((p, i) => {
            const half = points.length === 1 ? innerW / 2 : innerW / (points.length - 1) / 2;
            return (
              <g
                key={p.label}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                onClick={() => onSelect?.(p)}
                className={onSelect ? "cursor-pointer" : undefined}
              >
                <rect
                  x={Math.max(PAD.left, x(i) - half)}
                  y={PAD.top}
                  width={Math.min(half * 2, W - PAD.right - PAD.left)}
                  height={innerH}
                  fill="transparent"
                />
                <circle
                  cx={x(i)}
                  cy={y(p.cumulative)}
                  r={hover === i ? 5 : 3}
                  fill={SERIES_1}
                  opacity={hover === null || hover === i ? 1 : 0.55}
                />
              </g>
            );
          })}

          {points.map((p, i) =>
            i === 0 || i === points.length - 1 || i === Math.floor(points.length / 2) ? (
              <text
                key={p.label}
                x={x(i)}
                y={H - 10}
                textAnchor={i === 0 ? "start" : i === points.length - 1 ? "end" : "middle"}
                className="fill-muted-foreground"
                style={{ fontSize: 10 }}
              >
                {p.label}
              </text>
            ) : null,
          )}
        </svg>

        {/* Cumulative, not the month's own hours: that is what the line plots,
            and a tooltip reporting a different measure from the mark under the
            pointer is worse than no tooltip. */}
        {hover !== null && points[hover] ? (
          <div className="pointer-events-none absolute left-2 top-2 rounded-md border border-border bg-popover px-2 py-1 text-xs shadow-sm">
            <div className="font-medium">{points[hover].label}</div>
            <div className="text-muted-foreground">
              {hours(points[hover].cumulative)}h burned by this month
            </div>
          </div>
        ) : null}
      </div>
    </ChartFrame>
  );
}
